import type { RulebookChapter, FAQItem, QuickReferenceSection, GuidedGameStep } from './types';
import type { VinhosGlossaryItem } from './glossary';

/**
 * Content Rewrite 3.0 — ONE menu chapter per module (internal headings only).
 * Module chapters are gated by requiredModuleIds + special-vintage-2016.
 * Printed expert ability details beyond the listed eight remain on the tiles.
 */
export const vinhosModuleChapters: RulebookChapter[] = [
  {
    id: 'module-experts',
    title: 'Dodatek Experts',
    requiredModuleIds: ['experts'],
    requiredVariantIds: ['special-vintage-2016'],
    sections: [
      {
        id: 'experts-setup',
        title: 'Przygotowanie i wybór puli',
        content: [
          { type: 'heading', text: 'Co dodaje moduł i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Pakiet Experts dodaje 32 kafle ekspertów: po 4 egzemplarze ośmiu nowych zdolności. Dokłada się je do ekspertów z gry podstawowej. Poza przygotowaniem stosów i treścią zdolności podstawowe reguły zatrudniania, odwracania i odświeżania ekspertów pozostają bez zmian.',
          },
          { type: 'heading', text: 'Kiedy wybierać który wariant przygotowania', level: 2 },
          {
            type: 'paragraph',
            text: 'Przed partią wybieracie jeden sposób budowy czterech stosów cech. Wariant zmienia dostępność zdolności, nie zmienia momentu użycia eksperta w turze.',
          },
          { type: 'heading', text: 'Wariant zaskakujący', level: 3 },
          {
            type: 'bulletList',
            items: [
              'Dla każdej z czterech cech wina osobno przetasuj wszystkich ekspertów tej cechy.',
              'Utwórz stos z 7 losowych kafli; pozostałe odłóż do pudełka.',
              'Stosy mogą zawierać różne zestawy zdolności — wariant jest najbardziej zmienny, ale najmniej symetryczny.',
            ],
          },
          { type: 'heading', text: 'Wariant zbalansowany', level: 3 },
          {
            type: 'bulletList',
            items: [
              'Wybierz jedną cechę, przetasuj jej ekspertów i wylosuj 7 różnych zdolności.',
              'Dla trzech pozostałych cech utwórz stosy z dokładnie odpowiadających im 7 ekspertów.',
              'Przetasuj każdy stos. Wszystkie cechy mają ten sam zestaw zdolności.',
            ],
          },
          { type: 'heading', text: 'Wariant kontrolowany', level: 3 },
          {
            type: 'bulletList',
            items: [
              'Wybierz 7 zdolności, które mają pojawić się w partii.',
              'Dla każdej cechy utwórz stos z odpowiadających im kafli.',
              'Przetasuj stosy. Ten wariant polecany jest dopiero po poznaniu wszystkich ekspertów.',
            ],
          },
        ],
      },
      {
        id: 'experts-effects',
        title: 'Osiem nowych ekspertów',
        content: [
          { type: 'heading', text: 'Jak czytać zdolności', level: 2 },
          {
            type: 'paragraph',
            text: 'Każda zdolność działa w momencie wskazanym przez jej treść. Poniższa tabela streszcza zweryfikowane efekty; przy stole potwierdź symbol i timing na kaflu.',
          },
          {
            type: 'table',
            headers: ['Ekspert', 'Efekt'],
            rows: [
              [
                'Reklamodawcy',
                'W Special Vintage 2016, w fazie kupowania kafli akcji magnatów po targach, weź kafel akcji magnata bez odrzucania wina. To kafel specjalny używany wyłącznie w tej fazie.',
              ],
              ['Marketingowcy', 'Każda beczka umieszczona podczas tej akcji eksportu daje dodatkowo 1 VP.'],
              ['Sprzedawcy', 'Każda beczka umieszczona podczas tej akcji sprzedaży daje dodatkowo 1 bago.'],
              ['Szpiedzy', 'Użyj odkrytej zdolności eksperta należącego do innego gracza. Kafel przeciwnika pozostaje odkryty.'],
              ['Meteorolodzy', 'Podejrzyj w sekrecie następny kafel rocznika. Nie pokazuj go innym.'],
              ['Recenzenci', 'Przesuń swój znacznik o 2 pola na torze punktów targowych.'],
              [
                'Zarządcy posiadłości',
                'Przenieś jedną własną winiarnię do innej własnej posiadłości, zachowując legalne limity miejsc.',
              ],
              ['Rzecznicy prasowi', 'Wsuń ten kafel pod spód jego stosu i weź jednego eksperta z dowolnego stosu.'],
            ],
          },
          {
            type: 'warning',
            text: 'Zdolność eksperta rozpatruje się dokładnie w momencie wskazanym przez jej treść. Reklamodawca w wariancie 2016 nie może być użyty podczas zwykłej akcji ani poza fazą kupowania kafli magnatów.',
          },
        ],
      },
      {
        id: 'experts-interactions',
        title: 'Interakcje, przykład i błędy',
        content: [
          { type: 'heading', text: 'Przykład przy stole', level: 2 },
          {
            type: 'example',
            title: 'Marketingowiec na eksporcie',
            text: 'Odwracasz Marketingowca i wykonujesz akcję Exports. Umieszczasz dwie beczki na legalnych polach. Dostajesz zwykłe VP z pól oraz dodatkowo 2 VP (po 1 za każdą beczkę tej akcji). Premia nie dotyczy beczek położonych wcześniej w innych turach.',
          },
          { type: 'heading', text: 'Interakcje', level: 2 },
          {
            type: 'bulletList',
            items: [
              'Szpieg nie odwraca ani nie zużywa kafla przeciwnika — zużywany jest własny kafel Szpiega.',
              'Marketingowiec i Sprzedawca wzmacniają całą bieżącą akcję, więc premia dotyczy każdej beczki umieszczonej w ramach tej akcji.',
              'Zarządca przenosi winiarnię, nie tworzy nowej i nie dokłada renomy do regionu.',
              'Rzecznik prasowy sam trafia pod stos; pobrany kafel przechodzi do gracza na zwykłych zasadach.',
              'Meteorolog daje informację prywatną; wolno ją omawiać, ale nie wolno pokazywać kafla.',
            ],
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: uznać Szpiega za odwrócenie kafla rywala albo dać Marketingowcowi tylko 1 VP niezależnie od liczby beczek.',
          },
          {
            type: 'note',
            text: 'Odsyłacz: podstawowe zatrudnianie ekspertów opisuje rozdział „Eksperci winiarscy”. W Reserve 2010 ten sam moduł ma osobny rozdział r2010-module-experts (m.in. inna procedura Reklamodawcy).',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: '32 kafle, trzy sposoby przygotowania, osiem zdolności w momencie z tekstu. P: Czy Szpieg zużywa eksperta przeciwnika? O: Nie. P: Czy Marketingowiec daje stałe +1 VP? O: Nie — +1 VP za każdą beczkę tej akcji eksportu.',
          },
        ],
      },
    ],
  },
  {
    id: 'module-islands',
    title: 'Dodatek Islands',
    requiredModuleIds: ['islands'],
    requiredVariantIds: ['special-vintage-2016'],
    sections: [
      {
        id: 'islands-setup',
        title: 'Przygotowanie wysp',
        content: [
          { type: 'heading', text: 'Co dodaje moduł i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Moduł dodaje dwa opcjonalne regiony: Maderę i Azory. Można użyć jednego albo obu. Każdy region wyspiarski zastępuje jeden z regionów wyłączonych normalnie dla danej liczby graczy — nie powiększa mapy ponad limit liczby graczy.',
          },
          {
            type: 'numberedList',
            items: [
              'Wybierz Maderę, Azory albo oba regiony.',
              'Dla każdej użytej wyspy przypisz numer jednego z wyłączonych regionów, aby działały preferencje Caroliny i efekty odwołujące się do numeru regionu.',
              'Połóż kafel wina z przypisanym numerem na planszetce wyspy jako przypomnienie.',
              'Przygotuj odpowiednią planszetkę, stos winnic i elementy specjalne regionu.',
            ],
          },
        ],
      },
      {
        id: 'madeira',
        title: 'Madera',
        content: [
          { type: 'heading', text: 'Jak działa Madera', level: 2 },
          {
            type: 'paragraph',
            text: 'Madera to region wyspiarski z turystą i stałą premią pieniężną przy produkcji. Turysta nie podnosi produkcji — zwiększa wartość wina w chwili, gdy zabierasz je z odwiedzanej piwnicy.',
          },
          {
            type: 'bulletList',
            items: [
              'Zakup winnicy na Maderze kosztuje 6 bagos.',
              'Po zakupie umieść 1 turystę w jednej ze swoich piwnic. Jeżeli nie masz piwnicy, połóż go w magazynie posiadłości na Maderze.',
              'Turystę z magazynu Madery możesz przenieść do dowolnej własnej piwnicy wyłącznie podczas fazy produkcji.',
              'Podczas fazy produkcji turyści mogą przemieszczać się między twoimi piwnicami.',
              'Turysta w piwnicy zwiększa o 1 wartość każdego wina zabieranego z tej piwnicy.',
              'W każdej fazie produkcji otrzymujesz 1 bago z każdej swojej posiadłości na Maderze, niezależnie od obecności turysty.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład Madery',
            text: 'Kupujesz winnicę za 6 bagos i nie masz jeszcze piwnicy — turysta ląduje w magazynie Madery. W fazie produkcji przenosisz go do piwnicy w innej posiadłości. Przy eksporcie wina z tej piwnicy liczysz +1 do wartości. Dodatkowo bierzesz 1 bago za posiadłość na Maderze.',
          },
          {
            type: 'note',
            text: 'Premia turysty nie podnosi produkcji. Podnosi wartość wina w chwili, gdy wino opuszcza odwiedzaną piwnicę — np. przy sprzedaży, eksporcie lub wystawieniu na targi.',
          },
        ],
      },
      {
        id: 'azores',
        title: 'Azory',
        content: [
          { type: 'heading', text: 'Jak działają Azory', level: 2 },
          {
            type: 'paragraph',
            text: 'Azory dają specjalny magazyn, wybór koloru wina przy każdej produkcji i jednorazowy darmowy eksport z pola dojrzewania oznaczonego wartością 9. Nie budujesz tu piwnicy — cały tor to specjalny magazyn wyspy.',
          },
          {
            type: 'bulletList',
            items: [
              'Zakup winnicy na Azorach kosztuje 4 bagos.',
              'Weź specjalny magazyn z trzema polami i beczkę w swoim kolorze. Zakryj nim zwykły magazyn tej posiadłości i ustaw beczkę na trzecim polu.',
              'W każdej produkcji możesz niezależnie wybrać czerwone albo białe wino.',
              'Posiadłość na Azorach może składać się wyłącznie z winnic.',
              'Na Azorach nie można zbudować piwnicy.',
              'Gdy wino dojrzeje do pola z beczką oznaczonego wartością 9, możesz natychmiast wykonać darmowy eksport na pole o odpowiednim progu.',
              'Przy darmowym eksporcie nadal wolno użyć renomy Azorów do podniesienia wartości.',
              'Darmowy eksport można wykonać tylko raz na gracza — używa się do niego specjalnej beczki Azorów.',
            ],
          },
          {
            type: 'warning',
            text: 'Darmowy eksport jest natychmiastową korzyścią osiągnięcia wskazanego pola dojrzewania, nie osobną akcją na Quadrelu.',
          },
        ],
      },
      {
        id: 'islands-combinations',
        title: 'Przykład, interakcje i podsumowanie',
        content: [
          {
            type: 'example',
            title: 'Przykład Azorów',
            text: 'Produkujesz na Azorach białe wino. W kolejnych końcach roku przesuwa się po specjalnym magazynie. Gdy dojdzie do pola 9 z beczką, możesz natychmiast wykonać darmowy eksport (raz na grę tą beczką), bez zajmowania pola Exports na Quadrelu.',
          },
          {
            type: 'paragraph',
            text: 'Turysta otrzymany przy zakupie Madery jest tym samym typem pionka co turyści z Tasting Room. Jeżeli oba moduły są aktywne, stosuj wspólne zasady poruszania turystów i limit jednego turysty w piwnicy.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: dodać wyspy ponad normalną liczbę regionów zamiast zastąpić regiony wyłączone, albo traktować darmowy eksport Azorów jako akcję Quadrelu.',
          },
          {
            type: 'crossReference',
            chapterId: 'module-tasting-room',
            label: 'Turyści — Tasting Room',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Wyspy zastępują wyłączone regiony; Madera daje turystę i 1 bago; Azory dają wybór koloru i jednorazowy eksport z pola 9. P: Czy Madera daje 1 bago tylko z turystą? O: Nie. P: Czy darmowy eksport wymaga akcji? O: Nie.',
          },
        ],
      },
    ],
  },
  {
    id: 'module-tasting-room',
    title: 'Dodatek Tasting Room',
    requiredModuleIds: ['tasting-room'],
    requiredVariantIds: ['special-vintage-2016'],
    sections: [
      {
        id: 'tasting-setup',
        title: 'Przygotowanie',
        content: [
          { type: 'heading', text: 'Co dodaje moduł i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Tasting Room wprowadza turystów: pionki, które nie podnoszą produkcji, lecz zwiększają wartość wina zabieranego z odwiedzanej piwnicy. Elementy drewniane winiarni i butelek porto są kosmetyczne — nie zmieniają reguł.',
          },
          {
            type: 'numberedList',
            items: [
              'Odłóż tylu turystów, ile łącznie piwnic znajduje się w puli gry; uwzględnij także piwnice używane z Maderą.',
              'Dodaj turystów do wspólnej puli pracowników obok enologów i farmerów.',
              'Połóż na polu zatrudniania odpowiedni dla wybranego wariantu kafel „Zatrudnij enologów / farmerów / turystów”.',
              'Opcjonalne drewniane winiarnie i butelki porto zastępują odpowiadające im żetony wyłącznie wizualnie; nie zmieniają reguł.',
            ],
          },
        ],
      },
      {
        id: 'tourists',
        title: 'Zatrudnianie i ruch turystów',
        content: [
          { type: 'heading', text: 'Jak zatrudnić i kiedy działa efekt', level: 2 },
          {
            type: 'bulletList',
            items: [
              'Turystę zatrudnia się akcją pracowników. Koszt jednego turysty wynosi 2 bagos.',
              'Po zatrudnieniu umieść turystę na dowolnym polu jednej własnej posiadłości — także pustym.',
              'Turysta daje efekt wyłącznie wtedy, gdy odwiedza piwnicę.',
              'Może wejść do piwnicy natychmiast po przybyciu do posiadłości albo na początku fazy produkcji.',
              'Na początku produkcji może przemieścić się między własnymi posiadłościami i piwnicami tak jak pozostali pracownicy, z zachowaniem ograniczeń.',
              'W jednej piwnicy może znajdować się maksymalnie 1 turysta.',
            ],
          },
        ],
      },
      {
        id: 'tourist-effect',
        title: 'Premia, przykład i granice',
        content: [
          {
            type: 'paragraph',
            text: 'Kiedy wino zostaje zabrane z piwnicy odwiedzanej przez turystę, jego wartość wzrasta o 1 na potrzeby tej konkretnej czynności. Dotyczy to m.in. sprzedaży, eksportu i Press Release.',
          },
          {
            type: 'bulletList',
            items: [
              'Premia nie zwiększa wartości produkcji nowego wina.',
              'Premia nie zapisuje się na kaflu i nie pozostaje po przeniesieniu wina.',
              'Jeden turysta daje maksymalnie +1; nie można umieścić dwóch turystów w tej samej piwnicy.',
              'Turysta nie może wejść do podwójnej piwnicy z Connoisseur.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład sprzedaży z turystą',
            text: 'Wino w piwnicy ma wartość 6. W piwnicy stoi turysta. Sprzedajesz to wino na pole wymagające 7 — liczy się jako 7 tylko na potrzeby tej sprzedaży. Kafel nie „dostaje +1 na stałe”.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: doliczać turystę do produkcji albo stawiać dwóch turystów w jednej piwnicy.',
          },
        ],
      },
      {
        id: 'tasting-components',
        title: 'Elementy kosmetyczne i podsumowanie',
        content: [
          {
            type: 'bulletList',
            items: [
              'Drewniane winiarnie mogą zastępować kafle winiarni albo być używane razem z nimi jako znaczniki 3D.',
              'Butelki porto zastępują dwa żetony porto otrzymywane przy zakupie winnicy w Douro.',
              'Stojak na wino służy do przechowywania butelek porto.',
              'Butelki Deluxe dla Reserve 2010 zastępują znaczniki cech na targach; w Special Vintage 2016 nie mają funkcji mechanicznej.',
            ],
          },
          {
            type: 'note',
            text: 'Odsyłacz: interakcja z podwójną piwnicą jest w rozdziale Connoisseur. Turysta z Madery stosuje te same limity, gdy Islands jest aktywne.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Turysta kosztuje 2, działa tylko w piwnicy, daje +1 przy zabieraniu wina. P: Czy może stać na pustym polu? O: Tak, ale bez efektu. P: Czy podnosi produkcję? O: Nie.',
          },
        ],
      },
    ],
  },
  {
    id: 'module-connoisseur',
    title: 'Dodatek Connoisseur',
    requiredModuleIds: ['connoisseur'],
    requiredVariantIds: ['special-vintage-2016'],
    sections: [
      {
        id: 'connoisseur-setup',
        title: 'Przygotowanie',
        content: [
          { type: 'heading', text: 'Co dodaje moduł i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Connoisseur dodaje trzy podwójne kafle (winnica, winiarnia, piwnica) o silniejszych premiach i większym zajęciu miejsca oraz opcjonalny brutalny kafel rocznika Filoksera. Każdy gracz może mieć maksymalnie po jednym kaflu każdego rodzaju.',
          },
          {
            type: 'bulletList',
            items: [
              'Połóż obok planszy podwójną winnicę, podwójną winiarnię i podwójną piwnicę.',
              'Każdy gracz może posiadać maksymalnie po jednym kaflu każdego rodzaju.',
              'Kafle oceanu mogą zakryć nieużywane regiony; są wyłącznie pomocą wizualną.',
              'Opcjonalnie wtasuj kafel rocznika Filoksera. Polecany jest wyłącznie doświadczonym graczom.',
            ],
          },
        ],
      },
      {
        id: 'double-vineyard',
        title: 'Podwójna winnica (latyfundium)',
        content: [
          { type: 'heading', text: 'Czym jest i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Podwójna winnica mocno podnosi jakość produkcji (+3) i dodaje renomę, ale zajmuje dwa pola i blokuje miejsce na winiarnię w tej posiadłości. Kupujesz ją tylko wtedy, gdy świadomie rezygnujesz z dalszego rozwoju budynkami w tej kolumnie.',
          },
          {
            type: 'bulletList',
            items: [
              'Kupuje się ją akcją Winnice za 6 bagos.',
              'Po zakupie dodaj 1 kostkę renomy do powiązanego regionu.',
              'Zajmuje 2 pola posiadłości i musi zostać dołożona do posiadłości z istniejącą winnicą.',
              'Posiadłość z podwójną winnicą nie będzie miała miejsca na winiarnię.',
              'Daje +3 do jakości produkcji.',
              'Może pomieścić tylko 1 farmera.',
              'Każdy gracz może mieć tylko jedną podwójną winnicę.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład przy stole',
            text: 'Masz już zwykłą winnicę i wolne dwa pola. Kupujesz latyfundium za 6, dokładasz renomę i zamykasz miejsce na winiarnię. Posiadłość zyskuje +3 do produkcji, ale nie postawisz tu już winiarni ani drugiego farmera.',
          },
        ],
      },
      {
        id: 'double-winery',
        title: 'Podwójna winiarnia',
        content: [
          { type: 'heading', text: 'Czym jest i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Podwójna winiarnia daje silniejszą premię produkcji (+2) i renomę, zajmując dwa pola. Limit jednego enologa pozostaje — to nie jest „dwa sloty za jedną cenę”.',
          },
          {
            type: 'bulletList',
            items: [
              'Kupuje się ją akcją Winiarnie za 5 bagos.',
              'Po zakupie dodaj 1 kostkę renomy do powiązanego regionu.',
              'Zajmuje 2 pola posiadłości.',
              'Daje +2 do jakości produkcji.',
              'Może pomieścić tylko 1 enologa.',
              'Każdy gracz może mieć tylko jedną podwójną winiarnię.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład przy stole',
            text: 'Budujesz podwójną winiarnię za 5 bagos w posiadłości z wolnymi dwoma polami. Dostajesz +2 do produkcji i renomę. Zatrudniasz tylko jednego enologa — drugiego slotu nie ma.',
          },
        ],
      },
      {
        id: 'double-cellar',
        title: 'Podwójna piwnica',
        content: [
          { type: 'heading', text: 'Czym jest i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Podwójna piwnica przyspiesza dojrzewanie (tor +2/+2/+4/+6 zamiast standardowego) i dodaje renomę. Turyści z Tasting Room lub Madery nie mogą jej odwiedzać — premia turysty nie łączy się z tym torem.',
          },
          {
            type: 'bulletList',
            items: [
              'Kupuje się ją akcją Piwnice za 4 bagos.',
              'Po zakupie dodaj 1 kostkę renomy do powiązanego regionu.',
              'Każdy gracz może mieć tylko jedną podwójną piwnicę.',
              'Tor podnosi wartość dojrzewającego wina kolejno o +2, +2, +4 i +6 zamiast standardowych +1, +1, +3 i +5.',
              'Turyści nie mogą odwiedzać podwójnej piwnicy.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład dojrzewania',
            text: 'Wino wchodzi na tor podwójnej piwnicy. Po kolejnych końcach roku osiąga wyższe skoki wartości niż w zwykłej piwnicy. Turysta z Tasting Room musi stać w innej, zwykłej piwnicy — tu wejść nie może.',
          },
        ],
      },
      {
        id: 'double-scoring',
        title: 'Miejsce, punktowanie i Filoksera',
        content: [
          {
            type: 'paragraph',
            text: 'Każdy podwójny kafel liczy się jako jeden kafel na potrzeby punktowania, ale zajmuje dwa miejsca. Może domknąć posiadłość składającą się tylko z dwóch fizycznych kafli.',
          },
          { type: 'heading', text: 'Kafel rocznika Filoksera', level: 2 },
          {
            type: 'warning',
            text: 'To celowo brutalny wariant. Używaj go tylko w grupie, która zna grę i akceptuje rok bez produkcji.',
          },
          {
            type: 'bulletList',
            items: [
              'Wtasuj kafel do stosu roczników.',
              'Po jego odkryciu w tym roku nie odbywa się żadna produkcja.',
              'Nadal można wystawić na targi posiadane wcześniej wino.',
              'W tym roku magnaci akceptują wina dowolnego koloru, wartości i regionu.',
              'Gracz bez żadnego wina nie może wykonać Press Release i może całkowicie opuścić targi.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład Filoksery',
            text: 'W roku 5 wychodzi Filoksera. Nikt nie produkuje. Gracze z zapasem wina nadal robią Press Release i targi; magnaci przyjmują dowolne wino. Gracz bez wina pomija Press Release i może wypaść z targów.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: liczyć podwójny kafel jako dwa kafle przy punktowaniu albo wpuszczać turystę do podwójnej piwnicy.',
          },
          {
            type: 'note',
            text: 'Odsyłacz: turyści i limit piwnicy — rozdział Tasting Room. W Reserve 2010 ten sam moduł ma rozdział r2010-module-connoisseur.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Trzy podwójne kafle (max 1/rodzaju), silniejsze premie, Filoksera = brak produkcji. P: Jak liczy się podwójny kafel? O: Jako jeden kafel, dwa miejsca. P: Czy w Filokserze są targi? O: Tak, jeśli rok jest targowy — bez nowej produkcji.',
          },
        ],
      },
    ],
  },
];

export const vinhosModuleFaqItems: FAQItem[] = [
  {
    id: 'experts-balanced',
    question: 'Czym różnią się trzy sposoby przygotowania Experts?',
    answer:
      'Zaskakujący losuje osobne zestawy 7 ekspertów dla każdej cechy; zbalansowany używa tych samych 7 zdolności we wszystkich czterech stosach; kontrolowany pozwala grupie wybrać dokładnie 7 zdolności.',
    tags: ['experts', 'przygotowanie'],
    requiredModuleIds: ['experts'],
    requiredVariantIds: ['special-vintage-2016'],
    chapterId: 'module-experts',
  },
  {
    id: 'spy-target',
    question: 'Czy Szpieg odwraca eksperta przeciwnika?',
    answer:
      'Nie. Zużywasz własnego Szpiega, a kafel przeciwnika pozostaje odkryty i nadal dostępny właścicielowi.',
    tags: ['experts', 'szpieg'],
    requiredModuleIds: ['experts'],
    requiredVariantIds: ['special-vintage-2016'],
    chapterId: 'module-experts',
  },
  {
    id: 'marketer-each-barrel',
    question: 'Czy Marketingowiec daje tylko 1 VP?',
    answer:
      'Nie. Daje +1 VP za każdą beczkę umieszczoną podczas tej konkretnej akcji eksportu.',
    tags: ['experts', 'eksport'],
    requiredModuleIds: ['experts'],
    requiredVariantIds: ['special-vintage-2016'],
    chapterId: 'module-experts',
  },
  {
    id: 'madeira-tourist',
    question: 'Co robi turysta otrzymany z Madery, gdy nie mam piwnicy?',
    answer:
      'Trafia do magazynu posiadłości na Maderze. Dopiero podczas fazy produkcji możesz przenieść go do jednej ze swoich piwnic.',
    tags: ['islands', 'madeira', 'turysta'],
    requiredModuleIds: ['islands'],
    requiredVariantIds: ['special-vintage-2016'],
    chapterId: 'module-islands',
  },
  {
    id: 'madeira-income',
    question: 'Czy Madera daje 1 bago tylko z turystą?',
    answer:
      'Nie. W fazie produkcji otrzymujesz 1 bago z każdej posiadłości na Maderze niezależnie od obecności turysty.',
    tags: ['islands', 'madeira'],
    requiredModuleIds: ['islands'],
    requiredVariantIds: ['special-vintage-2016'],
    chapterId: 'module-islands',
  },
  {
    id: 'azores-color',
    question: 'Kiedy wybieram kolor wina z Azorów?',
    answer:
      'W każdej fazie produkcji wybierasz na nowo czerwone albo białe wino dla tej posiadłości.',
    tags: ['islands', 'azory'],
    requiredModuleIds: ['islands'],
    requiredVariantIds: ['special-vintage-2016'],
    chapterId: 'module-islands',
  },
  {
    id: 'azores-export',
    question: 'Czy darmowy eksport z Azorów wymaga akcji?',
    answer:
      'Nie. Uruchamia się natychmiast, gdy wino dojrzeje do oznaczonego pola wartości 9. Można go wykonać tylko raz specjalną beczką Azorów.',
    tags: ['islands', 'azory', 'eksport'],
    requiredModuleIds: ['islands'],
    requiredVariantIds: ['special-vintage-2016'],
    chapterId: 'module-islands',
  },
  {
    id: 'tourist-bonus-time',
    question: 'Kiedy turysta zwiększa wartość wina?',
    answer:
      'Gdy wino jest zabierane z odwiedzanej piwnicy do konkretnego użycia, np. sprzedaży, eksportu lub targów. Nie zwiększa produkcji i nie zmienia kafla na stałe.',
    tags: ['tasting-room', 'turysta'],
    requiredModuleIds: ['tasting-room'],
    requiredVariantIds: ['special-vintage-2016'],
    chapterId: 'module-tasting-room',
  },
  {
    id: 'tourist-empty-space',
    question: 'Czy turysta może stać na pustym polu posiadłości?',
    answer:
      'Tak, ale nie daje wtedy żadnego efektu. Korzyść działa wyłącznie w piwnicy.',
    tags: ['tasting-room', 'turysta'],
    requiredModuleIds: ['tasting-room'],
    requiredVariantIds: ['special-vintage-2016'],
    chapterId: 'module-tasting-room',
  },
  {
    id: 'tourist-double-cellar',
    question: 'Czy turysta może wejść do podwójnej piwnicy?',
    answer:
      'Nie. Podwójna piwnica z Connoisseur jest niedostępna dla turystów.',
    tags: ['tasting-room', 'connoisseur', 'turysta'],
    requiredModuleIds: ['tasting-room', 'connoisseur'],
    requiredVariantIds: ['special-vintage-2016'],
    chapterId: 'module-connoisseur',
  },
  {
    id: 'double-tile-count',
    question: 'Jak podwójne kafle liczą się przy punktowaniu?',
    answer:
      'Każdy podwójny kafel liczy się jako jeden kafel, mimo że zajmuje dwa miejsca. Może przez to domknąć posiadłość złożoną tylko z dwóch fizycznych kafli.',
    tags: ['connoisseur', 'punktowanie'],
    requiredModuleIds: ['connoisseur'],
    requiredVariantIds: ['special-vintage-2016'],
    chapterId: 'module-connoisseur',
  },
  {
    id: 'phylloxera-fair',
    question: 'Czy w roku Filoksery odbywają się targi?',
    answer:
      'Tak, jeśli przypadają w tym roku. Nie ma produkcji, lecz można wystawić wcześniej posiadane wino; magnaci akceptują wtedy dowolny kolor, wartość i region.',
    tags: ['connoisseur', 'filoksera', 'targi'],
    requiredModuleIds: ['connoisseur'],
    requiredVariantIds: ['special-vintage-2016'],
    chapterId: 'module-connoisseur',
  },
];

export const vinhosModuleQuickReference: QuickReferenceSection[] = [
  {
    id: 'qr-experts',
    title: 'Experts — skrót',
    content:
      'Wybierz sposób przygotowania stosów. Marketingowiec: +1 VP za każdą beczkę eksportu. Sprzedawca: +1 bago za każdą beczkę sprzedaży. Szpieg kopiuje odkrytego eksperta rywala bez odwracania go. Meteorolog podgląda kolejny rocznik. Recenzent: +2 FP. Zarządca przenosi winiarnię. Rzecznik wymienia się przez spód stosu. Reklamodawca bierze kafel magnata bez wina.',
    chapterId: 'module-experts',
    requiredModuleIds: ['experts'],
    requiredVariantIds: ['special-vintage-2016'],
  },
  {
    id: 'qr-islands',
    title: 'Islands — skrót',
    content:
      'Madera: winnica 6 bagos, turysta, +1 bago z posiadłości podczas produkcji. Azory: winnica 4 bagos, wybór koloru co produkcję, brak piwnicy, jednorazowy darmowy eksport po dojrzewaniu do pola 9.',
    chapterId: 'module-islands',
    requiredModuleIds: ['islands'],
    requiredVariantIds: ['special-vintage-2016'],
  },
  {
    id: 'qr-tasting',
    title: 'Tasting Room — skrót',
    content:
      'Turysta kosztuje 2 bagos. Może stać gdziekolwiek, ale działa tylko w piwnicy. Maks. 1 turysta na piwnicę. Wino zabierane z odwiedzanej piwnicy ma +1 do tej czynności. Brak wstępu do podwójnej piwnicy.',
    chapterId: 'module-tasting-room',
    requiredModuleIds: ['tasting-room'],
    requiredVariantIds: ['special-vintage-2016'],
  },
  {
    id: 'qr-connoisseur',
    title: 'Connoisseur — skrót',
    content:
      'Podwójna winnica: 6, +3, 1 farmer. Podwójna winiarnia: 5, +2, 1 enolog. Podwójna piwnica: 4, dojrzewanie +2/+2/+4/+6, bez turystów. Maks. 1 każdego rodzaju. Filoksera: brak produkcji.',
    chapterId: 'module-connoisseur',
    requiredModuleIds: ['connoisseur'],
    requiredVariantIds: ['special-vintage-2016'],
  },
];

export const vinhosModuleGuidedSteps: GuidedGameStep[] = [
  {
    id: 'guided-experts',
    title: 'Dodaj Experts',
    summary: 'Wybierz sposób przygotowania i zbuduj cztery stosy ekspertów.',
    checklist: [
      'Na pierwszą partię z modułem wybierz wariant zbalansowany.',
      'Wylosuj 7 zdolności.',
      'Zbuduj cztery odpowiadające sobie stosy.',
      'Przed rozpoczęciem przeczytaj osiem nowych zdolności.',
    ],
    whyTitle: 'Dlaczego zbalansowany?',
    whyContent:
      'Każda cecha ma identyczny zestaw możliwości, więc decyzje nie zależą od asymetrii stosów.',
    commonMistake:
      'Losowanie 7 całkowicie różnych ekspertów do każdego stosu i nazywanie tego wariantem zbalansowanym.',
    chapterId: 'module-experts',
    requiredModuleIds: ['experts'],
    requiredVariantIds: ['special-vintage-2016'],
  },
  {
    id: 'guided-islands',
    title: 'Dodaj wyspy',
    summary: 'Zastąp wyłączone regiony Maderą lub Azorami.',
    checklist: [
      'Przypisz każdej wyspie numer wyłączonego regionu.',
      'Połóż numer na planszetce wyspy.',
      'Przygotuj specjalny magazyn i beczkę Azorów.',
      'Przygotuj turystę Madery.',
    ],
    whyTitle: 'Po co numer regionu?',
    whyContent:
      'Efekty i preferencje Caroliny nadal muszą móc odwołać się do regionu wyspiarskiego.',
    commonMistake:
      'Dodanie wysp ponad normalną liczbę regionów zamiast zastąpienia regionów wyłączonych.',
    chapterId: 'module-islands',
    requiredModuleIds: ['islands'],
    requiredVariantIds: ['special-vintage-2016'],
  },
  {
    id: 'guided-tasting',
    title: 'Dodaj Tasting Room',
    summary: 'Przygotuj pulę turystów i kafel zatrudniania.',
    checklist: [
      'Policz piwnice w puli.',
      'Odłóż tyle samo turystów.',
      'Połóż właściwą stronę kafla zatrudniania.',
      'Przypomnij limit 1 turysty na piwnicę.',
    ],
    whyTitle: 'Dlaczego liczba zależy od piwnic?',
    whyContent:
      'Pula jest ograniczona maksymalną liczbą miejsc, w których turyści mogą dawać efekt.',
    commonMistake:
      'Dodanie wszystkich turystów z pudełka niezależnie od liczby piwnic.',
    chapterId: 'module-tasting-room',
    requiredModuleIds: ['tasting-room'],
    requiredVariantIds: ['special-vintage-2016'],
  },
  {
    id: 'guided-connoisseur',
    title: 'Dodaj Connoisseur',
    summary: 'Połóż trzy podwójne kafle i zdecyduj o Filokserze.',
    checklist: [
      'Przygotuj podwójną winnicę, winiarnię i piwnicę.',
      'Przypomnij limit jednego kafla każdego rodzaju na gracza.',
      'Zostaw Filokserę poza talią podczas pierwszej partii z modułem.',
      'Użyj kafli oceanu wyłącznie jako przykrywek.',
    ],
    whyTitle: 'Dlaczego bez Filoksery?',
    whyContent:
      'Rok bez produkcji radykalnie karze brak zapasu wina i nie jest dobrym pierwszym kontaktem z dodatkiem.',
    commonMistake:
      'Traktowanie podwójnego kafla jako dwóch kafli przy punktowaniu.',
    chapterId: 'module-connoisseur',
    requiredModuleIds: ['connoisseur'],
    requiredVariantIds: ['special-vintage-2016'],
  },
];

export const vinhosModuleGlossaryItems: VinhosGlossaryItem[] = [
  {
    id: 'latifundium',
    term: 'Latyfundium',
    definition:
      'Podwójna winnica z Connoisseur: zajmuje 2 miejsca, daje +3 do produkcji i mieści 1 farmera.',
    chapterId: 'module-connoisseur',
    requiredModuleIds: ['connoisseur'],
    requiredVariantIds: ['special-vintage-2016'],
  },
  {
    id: 'tourist',
    term: 'Turysta',
    definition:
      'Pionek z Tasting Room lub Madery. W odwiedzanej piwnicy daje +1 wartości winu zabieranemu z tej piwnicy.',
    chapterId: 'module-tasting-room',
    requiredModuleIds: ['tasting-room'],
    requiredVariantIds: ['special-vintage-2016'],
  },
  {
    id: 'phylloxera',
    term: 'Filoksera',
    definition:
      'Opcjonalny kafel rocznika Connoisseur, który całkowicie wyłącza produkcję w danym roku.',
    chapterId: 'module-connoisseur',
    requiredModuleIds: ['connoisseur'],
    requiredVariantIds: ['special-vintage-2016'],
  },
  {
    id: 'azores-free-export',
    term: 'Eksport azorski',
    definition:
      'Jednorazowy, natychmiastowy darmowy eksport po dojrzewaniu wina z Azorów do oznaczonego pola wartości 9.',
    chapterId: 'module-islands',
    requiredModuleIds: ['islands'],
    requiredVariantIds: ['special-vintage-2016'],
  },
  {
    id: 'balanced-experts',
    term: 'Zbalansowany wybór ekspertów',
    definition:
      'Sposób przygotowania Experts, w którym wszystkie cztery stosy zawierają te same 7 zdolności.',
    chapterId: 'module-experts',
    requiredModuleIds: ['experts'],
    requiredVariantIds: ['special-vintage-2016'],
  },
];
